// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.HighchartRegression.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

// based on https://github.com/streamlinesocial/highcharts-regression
// https://github.com/Tom-Alexander/regression-js/

import {
  isEmpty,
  isArray,
  isNumber,
  isUndefined,
  isNil,
  isDate,
  find,
  includes,
  fill,
  flow,
  flatMap,
  reduce,
  concat,
  map,
  pick,
  cloneDeep,
  groupBy,
  sortBy,
  uniqBy,
  get
} from 'lodash/fp';
import moment from 'moment';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { DATE_TIME_FORMAT as DATE_TIME_FORMATS } from 'logic/data/Izenda.TimeFormatConstants';
import { isChartType } from 'logic/data/Izenda.ReportPartChartConstants';

/**
 * Build regression line.
 * @param {Object} Highcharts Hightcharts object
 */
export default function Regression(Highcharts: any) {
  const linearType = 'linear';
  const categoryType = 'category';
  const datetimeType = 'datetime';
  const baseStep = 1;
  let period = baseStep;
  let minDistance = baseStep;

  const axisTypes = [
    {
      key: linearType,
      accept: source => !isNil(source) && !isDate(source.x) && !isUndefined(source.x),
      getXLabel: source => source.x,
      nextStep: (data, step, count) => data[data.length - 1][0] + (count - data.length + 1) * step
    },
    {
      key: categoryType,
      accept: source => !isNil(source) && !isDate(source.x) && isUndefined(source.x),
      getXLabel: source => source.xRawData,
      nextStep: (data, step, count) => data[data.length - 1][0] + (count - data.length + 1) * step
    },
    {
      key: datetimeType,
      accept: source => !isNil(source) && isDate(source.x) && !isUndefined(source.x),
      getXLabel: source => source.x,
      nextStep: (data, step, count, intervalKey) => {
        let stepBasedOnIntervalKey = baseStep;
        const durationStepInMS = moment.duration(step, 'ms');
        switch (intervalKey) {
          case DATE_TIME_FORMATS['Qtr'].INTERVAL_VALUE:
          case DATE_TIME_FORMATS['yyyy - Qtr'].INTERVAL_VALUE:
            const monthsInAQuarter = 3;
            stepBasedOnIntervalKey = Math.round(durationStepInMS.as('M') / monthsInAQuarter);
            break;

          default:
            stepBasedOnIntervalKey = durationStepInMS.as(intervalKey);
            break;
        }
        return Date.parse(
          moment(data[data.length - 1]['x']).add((count - data.length + 1) * stepBasedOnIntervalKey, intervalKey)
        );
      }
    }
  ];

  const processSerie = s => {
    if (s.regression && !s.rendered) {
      s.regressionSettings = s.regressionSettings || {};
      s.regressionSettings.tooltip = s.regressionSettings.tooltip || {};
      s.regressionSettings.dashStyle = s.regressionSettings.dashStyle || 'solid';
      s.regressionSettings.decimalPlaces = s.regressionSettings.decimalPlaces || 2;
      s.regressionSettings.useAllSeries = s.regressionSettings.useAllSeries || false;

      const regressionType = s.regressionSettings.type || 'linear';

      let regression;
      const extraSerie = {
        data: [],
        color: s.regressionSettings.color || '',
        yAxis: s.yAxis,
        lineWidth: s.regressionSettings.lineWidth || 2,
        marker: { enabled: false },
        isRegressionLine: true,
        visible: s.regressionSettings.visible,
        type: s.regressionSettings.linetype || 'spline',
        name: s.regressionSettings.name || 'Equation: %eq',
        id: s.regressionSettings.id,
        dashStyle: s.regressionSettings.dashStyle || 'solid',
        showInLegend: !s.regressionSettings.hideInLegend,
        tooltip: {
          valueSuffix: s.regressionSettings.tooltip.valueSuffix || ' '
        },
        extrapolate: s.regressionSettings.extrapolate || 0,
        xAxisField: s.xAxisField,
        yAxisField: s.yAxisField,
        colorByPoint: false
      };

      let mergedData = s.data;
      if (s.regressionSettings.useAllSeries) {
        mergedData = [];
        for (let di = 0; di < series.length; di++) {
          const seriesToMerge = series[di];
          mergedData = mergedData.concat(seriesToMerge.data);
        }
      }

      mergedData = numberingData(mergedData);

      if (regressionType === 'linear' && extraSerie.extrapolate === 0) {
        regression = _linear(mergedData, s.regressionSettings.decimalPlaces);
        extraSerie.type = 'line';
      } else if (regressionType === 'exponential') {
        regression = _exponential(mergedData, s.regressionSettings.decimalPlaces);
      } else if (regressionType === 'polynomial' || (regressionType === 'linear' && extraSerie.extrapolate > 0)) {
        let order = s.regressionSettings.order || 2;
        regressionType === 'linear' && (order = 1);
        const reportPartElm = s.xAxisField.reportPartElm;
        const fieldFormatData = reportPartElm.fieldFormatData || get('format', reportPartElm.getValueByKey('format'));
        const intervalKey = get('INTERVAL_VALUE', DATE_TIME_FORMATS[fieldFormatData]);
        regression = _polynomial(
          mergedData,
          order,
          extraSerie.extrapolate,
          s.regressionSettings.decimalPlaces,
          intervalKey
        );
      } else if (regressionType === 'logarithmic') {
        regression = _logarithmic(mergedData, s.regressionSettings.decimalPlaces);
      }

      regression.rSquared = coefficientOfDetermination(mergedData, regression.points);
      regression.rValue = Math.sqrt(regression.rSquared).toFixed(s.regressionSettings.decimalPlaces);
      regression.rSquared = regression.rSquared.toFixed(s.regressionSettings.decimalPlaces);
      regression.standardError = standardError(mergedData, regression.points).toFixed(
        s.regressionSettings.decimalPlaces
      );
      extraSerie.data = regression.points;
      extraSerie.name = extraSerie.name.replace('%r2', regression.rSquared);
      extraSerie.name = extraSerie.name.replace('%r', regression.rValue);
      extraSerie.name = extraSerie.name.replace('%eq', regression.string);
      extraSerie.name = extraSerie.name.replace('%se', regression.standardError);
      extraSerie.regressionOutputs = regression;

      return extraSerie;
    }
  };

  Highcharts.wrap(Highcharts.Chart.prototype, 'init', function(proceed) {
    const { series: originsSeries } = arguments[1];
    const chartType = get('sort.chartType', arguments[1]);
    const extraSeries = [];
    let extrapolateCategoryCount = 0;
    resetStep();
    let series = cloneDeep(originsSeries);
    series = parseData(series, chartType);
    for (let i = 0; i < series.length; i++) {
      const s = series[i];
      if (shouldCalculateRegression(s, chartType)) {
        const extraSerie = processSerie(s);
        if (extraSerie) {
          extraSeries.push(extraSerie);
          extrapolateCategoryCount = Math.max(extraSerie.extrapolate, extrapolateCategoryCount);
        }
        arguments[1].series[i].rendered = true;
      }
    }

    if (extraSeries.length > 0) {
      arguments[1].series = originsSeries.concat(extraSeries);

      if (!isNil(arguments[1].xAxis.categories)) {
        let extrapolateCategories = new Array(extrapolateCategoryCount);
        extrapolateCategories = fill(0, extrapolateCategories.length, '', extrapolateCategories);
        arguments[1].xAxis.categories = arguments[1].xAxis.categories.concat(extrapolateCategories);
      }
    }

    proceed.apply(this, Array.prototype.slice.call(arguments, 1));
  });

  Highcharts.wrap(Highcharts.Chart.prototype, 'addSeries', function(proceed) {
    const s = arguments[1];
    resetStep();
    const extraSerie = processSerie(s);
    arguments[1].rendered = true;
    if (extraSerie) {
      this.addSeries(extraSerie);
    }
    proceed.apply(this, Array.prototype.slice.call(arguments, 1));
  });

  /**
   * Prepare data for bubble/scatter
   * @param {Array<Object>} series
   * @param {string} type
   */
  function parseData(series: Array<any>, type: string) {
    if (isNil(type) || isEmpty(series) || !isArray(series) || !isChartType(type, CHART_TYPE.Bubble, CHART_TYPE.Scatter))
      return series;
    return flow(
      groupBy(s => s.nameFieldName),
      map(g => {
        const data = flow(
          flatMap(s => s.data),
          reduce((result, items) => concat(result, items), []),
          map(item => pick(['name', 'record', 'x', 'xRawData', 'y', 'yRawData', 'z'], item)),
          uniqBy(r => r.x),
          sortBy(r => r.x)
        )(g);

        g[0].data = data;
        return g[0];
      })
    )(series);
  }

  /**
   * Resolve axis type
   * @param {Object} record
   */
  function getAxisType(record: any) {
    return find(type => type.accept(record), axisTypes);
  }

  /**
   * Check if should calculate regression line
   * @param {Object} serie
   */
  function shouldCalculateRegression(serie, type) {
    return (
      !isNil(type) &&
      (isChartType(type, CHART_TYPE.Bubble, CHART_TYPE.Scatter) ||
        (get('length', serie.data) > 1 &&
          isChartType(
            type,
            CHART_TYPE.Line,
            CHART_TYPE.Column,
            CHART_TYPE.Bar,
            CHART_TYPE.Area,
            CHART_TYPE.Combination
          )))
    );
  }

  /**
   * Calculate distance between 2 points on x axis.
   * @param {Object} operand1
   * @param {Object} operand2
   */
  function calculateDistance(operand1: any, operand2: any) {
    const matchingType = getAxisType(operand1);
    if (matchingType.key === categoryType) return baseStep;
    return operand2.x - operand1.x;
  }

  /**
   * Numbering x value to build regression line for nonnumeric data
   * @param {Array<Object>} data
   */
  function numberingData(data: Array<any>) {
    if (isEmpty(data) || !isArray(data)) return data;
    const distances = [];
    let step = Math.pow(2, 53) - 1; // Number.MAX_SAFE_INTEGER
    for (let i = 1; i < data.length; i++) {
      const distance = calculateDistance(data[i - 1], data[i]);
      distances.push({ index: i, distance });
      step = Math.min(step, distance);
    }

    data[0].tempX = 1;
    for (let j = 0; j < distances.length; j++) {
      data[distances[j].index].tempX = data[distances[j].index - 1].tempX + distances[j].distance / step;
    }

    if (!isEmpty(distances)) {
      minDistance = step;
      period = distances[distances.length - 1].distance;
    }
    return data;
  }

  /**
   * Reset step setting.
   */
  function resetStep() {
    period = baseStep;
    minDistance = baseStep;
  }

  /**
   * Resolve xAxis value to calculating
   * @param {Object} record
   */
  function xValue(record: any) {
    return isNumber(record.x) ? record.x : record.tempX;
  }

  /**
   * Resolve xAxis value for highcharts' series
   * @param {Object} record
   */
  function xLabel(record: any) {
    if (isNil(record)) return undefined;
    const matchingType = getAxisType(record);
    return matchingType.getXLabel(record);
  }

  /**
   * Check whether it is category type.
   * @param {Object} record
   */
  function isCategoryType(record: any) {
    const matchingType = getAxisType(record);
    return matchingType.key === categoryType;
  }

  /**
   * Compare two values
   * @param {Object} operand1
   * @param {Object} operand2
   */
  function compare(operand1: any, operand2: any) {
    if (operand1[0] > operand2[0]) {
      return 1;
    }
    if (operand1[0] < operand2[0]) {
      return -1;
    }
    return 0;
  }

  /**
   * Build exponential regression detail
   * @param {Array<Object>} data
   * @param {Number} decimalPlaces
   */
  function _exponential(data: Array<any>, decimalPlaces: Number) {
    let sum = [0, 0, 0, 0, 0, 0],
      results = [];

    for (let len = data.length, n = 0; n < len; n++) {
      if (!isNil(xLabel(data[n]))) {
        data[n][0] = xValue(data[n]);
        data[n][1] = data[n].y;
      }
      if (data[n][1] != null) {
        sum[0] += data[n][0];
        sum[1] += data[n][1];
        sum[2] += data[n][0] * data[n][0] * data[n][1];
        sum[3] += data[n][1] * Math.log(data[n][1]);
        sum[4] += data[n][0] * data[n][1] * Math.log(data[n][1]);
        sum[5] += data[n][0] * data[n][1];
      }
    }

    const denominator = sum[1] * sum[2] - sum[5] * sum[5];
    const A = Math.pow(Math.E, (sum[2] * sum[3] - sum[5] * sum[4]) / denominator);
    const B = (sum[1] * sum[4] - sum[5] * sum[3]) / denominator;

    const categoryType = isCategoryType(data[0]);
    for (let i = 0, len = data.length; i < len; i++) {
      let coordinateY = A * Math.pow(Math.E, B * data[i][0]);
      if (decimalPlaces) coordinateY = parseFloat(coordinateY.toFixed(decimalPlaces));
      const coordinate = categoryType ? coordinateY : { x: xLabel(data[i]), y: coordinateY };
      results.push(coordinate);
    }

    const fnValue = `y = ${Math.round(A * 100) / 100}e^(${Math.round(B * 100) / 100}x)`;
    return { equation: [A, B], points: results, string: fnValue };
  }

  /**
   * Build linear regression detail
   * @param {Array<Object>} data
   * @param {Number} decimalPlaces
   */
  function _linear(data: Array<any>, decimalPlaces: Number) {
    let sum = [0, 0, 0, 0, 0],
      results = [],
      N = data.length;

    for (let n = 0; n < data.length; n++) {
      if (!isNil(xLabel(data[n]))) {
        data[n][0] = xValue(data[n]);
        data[n][1] = data[n].y;
      }
      if (data[n][1] != null) {
        sum[0] += data[n][0];
        sum[1] += data[n][1];
        sum[2] += data[n][0] * data[n][0];
        sum[3] += data[n][0] * data[n][1];
        sum[4] += data[n][1] * data[n][1];
      } else {
        N -= 1;
      }
    }

    const gradient = (N * sum[3] - sum[0] * sum[1]) / (N * sum[2] - sum[0] * sum[0]);
    const intercept = sum[1] / N - (gradient * sum[0]) / N;
    const categoryType = isCategoryType(data[0]);

    for (let i = 0, len = data.length; i < len; i++) {
      let coordinateY = data[i][0] * gradient + intercept;
      if (decimalPlaces) coordinateY = parseFloat(coordinateY.toFixed(decimalPlaces));
      const coordinate = categoryType ? coordinateY : { x: xLabel(data[i]), y: coordinateY };
      results.push(coordinate);
    }

    const fnValue = `y = ${Math.round(gradient * 100) / 100}x + ${Math.round(intercept * 100) / 100}`;
    return {
      equation: [gradient, intercept],
      points: results,
      string: fnValue
    };
  }

  /**
   * Build logarithmic regression detail
   * @param {Array<Object>} data
   * @param {Number} decimalPlaces
   */
  function _logarithmic(data: Array<any>, decimalPlaces: Number) {
    let sum = [0, 0, 0, 0],
      n = 0,
      results = [];

    for (let len = data.length; n < len; n++) {
      if (!isNil(xLabel(data[n]))) {
        data[n][0] = xValue(data[n]);
        data[n][1] = data[n].y;
      }
      if (data[n][1] != null) {
        sum[0] += Math.log(data[n][0]);
        sum[1] += data[n][1] * Math.log(data[n][0]);
        sum[2] += data[n][1];
        sum[3] += Math.pow(Math.log(data[n][0]), 2);
      }
    }

    const B = (n * sum[1] - sum[2] * sum[0]) / (n * sum[3] - sum[0] * sum[0]);
    const A = (sum[2] - B * sum[0]) / n;
    const categoryType = isCategoryType(data[0]);

    for (let i = 0, len = data.length; i < len; i++) {
      let coordinateY = A + B * Math.log(data[i][0]);
      if (decimalPlaces) coordinateY = parseFloat(coordinateY.toFixed(decimalPlaces));
      const coordinate = categoryType ? coordinateY : { x: xLabel(data[i]), y: coordinateY };
      results.push(coordinate);
    }

    const fnValue = `y = ${Math.round(A * 100) / 100} + ${Math.round(B * 100) / 100} ln(x)`;
    return { equation: [A, B], points: results, string: fnValue };
  }

  /**
   * Build polynomial regression detail
   * @param {Array<Object>} data
   * @param {Number} order
   * @param {Number} extrapolate
   * @param {Number} decimalPlaces
   * @param {String} intervalKey
   */
  function _polynomial(
    data: Array<any>,
    order: Number,
    extrapolate: Number,
    decimalPlaces: Number,
    intervalKey: String
  ) {
    if (isUndefined(order)) {
      order = 2;
    }
    let lhs = [],
      rhs = [],
      results = [],
      a = 0,
      b = 0,
      k = order + 1;

    for (let i = 0; i < k; i++) {
      for (let l = 0, len = data.length; l < len; l++) {
        if (!isNil(xLabel(data[l]))) {
          data[l][0] = xValue(data[l]);
          data[l][1] = data[l].y;
        }
        if (data[l][1] != null) {
          a += Math.pow(data[l][0], i) * data[l][1];
        }
      }
      lhs.push(a);
      a = 0;
      const c = [];
      for (let j = 0; j < k; j++) {
        for (let l = 0, len = data.length; l < len; l++) {
          if (data[l][1]) {
            b += Math.pow(data[l][0], i + j);
          }
        }
        c.push(b);
        b = 0;
      }
      rhs.push(c);
    }
    rhs.push(lhs);

    const equation = gaussianElimination(rhs, k);
    const resultLength = data.length + extrapolate;
    const matchingType = getAxisType(data[0]);
    const firstXValue = data[0]['x'];
    for (let m = 0, len = resultLength; m < len; m++) {
      let answer = 0;
      let x = 0;
      let xExtrapolate;
      if (typeof data[m] !== 'undefined') {
        x = data[m][0];
        xExtrapolate = x;
      } else {
        x = matchingType.nextStep(data, period, m, intervalKey);
        matchingType.key === datetimeType && (x = new Date(x));
        xExtrapolate = matchingType.key === datetimeType ? (x - firstXValue) / minDistance + 1 : x;
      }

      for (let w = 0; w < equation.length; w++) {
        answer += equation[w] * Math.pow(xExtrapolate, w);
      }

      if (decimalPlaces) answer = parseFloat(answer.toFixed(decimalPlaces));
      switch (matchingType.key) {
        case linearType:
          results.push([xLabel(data[m]) || x, answer]);
          break;
        case categoryType:
          results.push([answer]);
          break;
        case datetimeType:
          results.push({ x: xLabel(data[m]) || x, y: answer });
          break;
      }
    }

    let fnValue = 'y = ';
    for (let n = equation.length - 1; n >= 0; n--) {
      if (n > 1) fnValue += `${Math.round(equation[n] * 100) / 100}x^${n} + `;
      else if (n === 1) fnValue += `${Math.round(equation[n] * 100) / 100}x + `;
      else fnValue += Math.round(equation[n] * 100) / 100;
    }

    return { equation: equation, points: results, string: fnValue };
  }

  /**
   * Calculate gaussian elimination
   * @param {Array<Object>} data
   * @param {Number} order
   */
  function gaussianElimination(data: Array<any>, order: Number) {
    let i = 0,
      j = 0,
      k = 0,
      maxrow = 0,
      tmp = 0,
      n = data.length - 1,
      x = new Array(order);
    for (i = 0; i < n; i++) {
      maxrow = i;
      for (j = i + 1; j < n; j++) {
        if (Math.abs(data[i][j]) > Math.abs(data[i][maxrow])) maxrow = j;
      }
      for (k = i; k < n + 1; k++) {
        tmp = data[k][i];
        data[k][i] = data[k][maxrow];
        data[k][maxrow] = tmp;
      }
      for (j = i + 1; j < n; j++) {
        for (k = n; k >= i; k--) {
          data[k][j] -= (data[k][i] * data[i][j]) / data[i][i];
        }
      }
    }
    for (j = n - 1; j >= 0; j--) {
      tmp = 0;
      for (k = j + 1; k < n; k++) tmp += data[k][j] * x[k];
      x[j] = (data[n][j] - tmp) / data[j][j];
    }
    return x;
  }

  /**
   * Resolve predicted's Y
   * @param {Object} predictPoint
   */
  function resolveYPred(predictPoint: any) {
    return !isUndefined(predictPoint[1])
      ? predictPoint[1]
      : !isUndefined(predictPoint['y'])
        ? predictPoint['y']
        : predictPoint;
  }

  /**
   * Calculate coefficient of determination
   * @param {Array<Object>} data
   * @param {Array<Object>} pred
   */
  function coefficientOfDetermination(data: Array<any>, pred: Array<any>) {
    // Sort the initial data { pred array (model's predictions) is sorted  }
    // The initial data must be sorted in the same way in order to calculate the coefficients
    data.sort(compare);

    // Calc the mean
    let mean = 0;
    let N = data.length;
    for (let i = 0; i < data.length; i++) {
      if (data[i][1] != null) {
        mean += data[i][1];
      } else {
        N--;
      }
    }
    mean /= N;

    // Calc the coefficent of determination
    let SSE = 0;
    let SSYY = 0;
    let predY;
    for (let i = 0; i < data.length; i++) {
      if (data[i][1] != null) {
        predY = resolveYPred(pred[i]);
        SSYY += Math.pow(data[i][1] - predY, 2);
        SSE += Math.pow(data[i][1] - mean, 2);
      }
    }
    return 1 - SSYY / SSE;
  }

  /**
   * Calculate standard error
   * @param {Array<Object>} data
   * @param {Array<Object>} pred
   */
  function standardError(data: Array<any>, pred: Array<any>) {
    let SE = 0,
      N = data.length,
      predY;

    for (let i = 0; i < data.length; i++) {
      if (data[i][1] != null) {
        predY = predY = resolveYPred(pred[i]);
        SE += Math.pow(data[i][1] - predY, 2);
      } else {
        N--;
      }
    }
    SE = Math.sqrt(SE / (N - 2));

    return SE;
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/highcharts/Izenda.HighchartRegression.js